module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('section', {
    staticClass: "chapter",
    attrs: {
      "epub:type": "chapter",
      "id": "ch02"
    }
  }, [_c('h1', {
    staticClass: "cn"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p32"
    }
  }), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/nyugross0005#rch02"
    }
  }, [_vm._v("1")])]), _vm._v(" "), _c('h1', {
    staticClass: "ct"
  }, [_vm._v("Food in the Biblical Era")]), _vm._v(" "), _c('p', {
    staticClass: "au"
  }, [_c('span', {
    staticClass: "sm"
  }, [_vm._v("ELAINE")]), _vm._v(" "), _c('span', {
    staticClass: "sm"
  }, [_vm._v("ADLER")]), _vm._v(" "), _c('span', {
    staticClass: "sm"
  }, [_vm._v("GOODFRIEND")])]), _vm._v(" "), _c('p', {
    staticClass: "pf"
  }, [_vm._v("The Garden of Eden was filled with fruit trees “pleasing to the sight and good for food,” according to the Hebrew Bible’s account of the first phase of human history. God allowed Adam and Eve to eat from all the trees in the garden except one, and when they ate from the prohibited tree, they were punished, exiled, and required to grow food laboriously by the sweat of their brows. This biblical story foreshadows the logic of the dietary rules later transmitted by Moses. God permits the consumption of animals easily available in and native to the natural environment but forbids, with no explanation, some commonly available animals and food mixtures. Those disobeying these decrees would be punished by bad harvests and exile. The reward for obedience—the dream—was living in a land in which one could effortlessly find and enjoy a bountiful supply of food. The present chapter shows not only what the Hebrew Bible and postbiblical apocryphal literature, in addition to archeological evidence, reveal about the actual foodways in the society known as “ancient Israel” but also how food was used to demonstrate the Israelites’ unique relationship to God, to the land, and to each other. For the thousand years beginning in the Iron Age (1200 BCE) and extending to the Hellenistic era (approximately the second century BCE), the Israelite diet appears to be shaped by ecological pragmatism—that is, by what would be available in a challenging environment—and yet religious and cultural factors intervene to mark some edible animals and food mixtures as taboo.")]), _vm._v(" "), _c('p', [_vm._v("The ancient foodways, especially as they are expressed in the Hebrew Bible, provide the template for later ways of considering and consuming food. First, the Pentateuch, or first five books of the Hebrew Bible—often referred to as “Torah” by Jews—presents food as the subject of divine law "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p33"
    }
  }), _vm._v("commanded by YHWH, Israel’s God, and says that God will reward or punish Israel according to its adherence to these and the rest of God’s laws. Belief in the need to eat in conformance with God’s laws is passed down the generations. Second, the Torah commands that Israelites offer gifts to God from their produce and livestock, a practice abandoned in the postbiblical era but symbolically performed in later Jewish worship, ritual, and study. Third, food in the Hebrew Bible serves as a means to celebrate sacred occasions and probably also major life events. In the postbiblical era, the celebratory function of food continues, and the centrality of food to Jewish culture survives through the modern era even as religious observance declines. Fourth, whereas the Hebrew Bible alludes to the social function of food laws in distinguishing or separating Israelites from other peoples, in the postbiblical era, this theme is expanded into a central element of religious practice. Fifth, these Jewish food traditions influence the formation of Christianity and Islam, whose leaders rejected or adapted them for their unique forms of religious expressions.")]), _vm._v(" "), _c('p', [_vm._v("Finally, although the food laws are described as vehicles for making ancient Israel a holy nation, they address ethical ideals fundamental to world religions and moral philosophies. Implicitly or explicitly, biblical food-centered texts teach concern for animal suffering, the need for careful stewardship of the earth, the importance of hospitality to strangers, and the obligation to feed the poor. The actual historical context of these ancient foodways shows how ethical ideals were inculcated—and could be today, something explored in part 3 of this book—through daily routines and weekly and seasonal celebrations and commemorations.")]), _vm._v(" "), _c('p', [_vm._v("Certain limitations in our knowledge of ancient Israelite food practices must be admitted from the outset. The Hebrew Bible, the major source of information for life in ancient Israel, is an anthology of literature that covers more than one thousand years and includes many mostly anonymous authors, the chronological context of whom is widely debated by modern scholars. While knowing the authorship of a literary section would help us understand the writer’s context and agenda and provide greater data about the evolution of foodways, this knowledge is hard to establish. Another limitation of the Hebrew Bible as a source is that it does not offer a complete snapshot of the Israelite diet, partly because of its focus on the royal and priestly segments of society. Further, we cannot be sure when and to what extent biblical laws "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p34"
    }
  }), _vm._v("were imposed or practiced. Our knowledge of how food was processed and who produced it is also limited. Therefore, the evidence offered by the Hebrew Bible must be supplemented. Archeology offers unwritten materials such as artifacts and structures that tell us how food was processed and stored, or it reveals animal bones that offer insight into which animals were consumed. Other sources of information include written documents about food supplies; comparative data from other cultures of the ancient world, such as Egypt, Mesopotamia, or Greece; and information offered by modern anthropology regarding traditional cultures in the region."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en49",
      "id": "en49r"
    }
  }, [_vm._v("1")])]), _vm._v(" Finally, the land’s geography and ecology were of paramount importance. Ancient Israelites—only by the end of this era is the designation “Jews” used—resided in the center of the Fertile Crescent. The Hebrew Bible called it Canaan and then “the Land of Israel.” Its ancient borders coincide approximately with the borders of modern Israel, with the addition of the land immediately east and west of the Jordan River. It shares the same climate as other regions in the Mediterranean basin, with its mild, wet winters and warm, dry summers. The Land of Israel consists of three vertical parallel zones from west to east: the coastal plain, the central hill country, and the Jordan River valley (containing the Sea of Galilee, Jordan River, and Dead Sea), with its eastern Transjordanian plateau. The central hill country—known as Judah and Samaria—was historically the most important, as it constituted the heartland of biblical Israel. Because of its limited water resources, hilly spine, and thin and rocky topsoil, this area is best suited for olives, grapes, sheep, and goats; the cultivation of grain is more tenuous. Israelites practiced “dry farming”—that is, reliance on rainfall with no artificial irrigation. This stands in contrast to its neighbors Egypt and Mesopotamia, which are both river valleys and flood plains and therefore could serve as food sources for Israelites in bad times."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en50",
      "id": "en50r"
    }
  }, [_vm._v("2")])]), _vm._v(" Even with all these sources of information, the modern scholar’s understanding of the ancient reality is still somewhat speculative.")]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0201"
    }
  }, [_vm._v("Basic Foods and God’s Blessings")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("The biblical text repeatedly indicates that the supply of three basic foods was synonymous with the divine blessing that would be earned "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p35"
    }
  }), _vm._v("by obeying God’s laws: “If, then, you obey the commandments that I enjoin upon you this day. . . . I will grant the rain for your land in season, the early rain and the late. You shall gather in your new "), _c('i', [_vm._v("grain")]), _vm._v(" and "), _c('i', [_vm._v("wine")]), _vm._v(" and "), _c('i', [_vm._v("oil")]), _vm._v(".”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en51",
      "id": "en51r"
    }
  }, [_vm._v("3")])]), _vm._v(" Grain (wheat and barley), olive oil, and wine are known as the “Mediterranean triad” because of their prevalence in the Mediterranean basin. This often-repeated triad, usually in the poetic form of "), _c('i', [_vm._v("dagan")]), _vm._v(" (grain), "), _c('i', [_vm._v("yits-har")]), _vm._v(" (freshly pressed olive oil), and "), _c('i', [_vm._v("tirosh")]), _vm._v(" (grape juice before fermentation), formed the basis of the daily diet, and all three were fundamental elements of festive meals."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en52",
      "id": "en52r"
    }
  }, [_vm._v("4")])])]), _vm._v(" "), _c('p', [_vm._v("Grain provided most of the calories consumed by Israelites on a daily basis, and wheat was the preferred grain. Cultivated in the fertile coastal and inland valleys, wheat cost twice as much as barley, which could tolerate a more hostile environment—less rain, poorer soil."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en53",
      "id": "en53r"
    }
  }, [_vm._v("5")])]), _vm._v(" Both grains were consumed in various forms: raw and unprocessed, boiled into porridge, roasted, or ground into flour for bread. Bread was so important to the Israelite diet that the word for bread, "), _c('i', [_vm._v("lehem")]), _vm._v(", frequently signifies food in general in the Hebrew Bible. Bread also plays a prominent role in the official religion of Israel in the guise of the “showbread,” the twelve loaves of unleavened bread that were placed weekly on the table inside the Tent of Meeting. Further, grain donations could be made to the Sanctuary as an independent sacrifice or to accompany an animal offering."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en54",
      "id": "en54r"
    }
  }, [_vm._v("6")])])]), _vm._v(" "), _c('p', [_vm._v("The making of bread was a time-consuming daily task. Wheat berries were ground in a large concave stone basin with a smaller convex upper stone, and for a family of five, this could take as many as three hours per day."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en55",
      "id": "en55r"
    }
  }, [_vm._v("7")])]), _vm._v(" Water or oil and salt were added to the flour, and the mixture was baked usually without leavening, akin to today’s pita bread. Leavening was not allowed when bread was made for sacrificial purposes."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en56",
      "id": "en56r"
    }
  }, [_vm._v("8")])]), _vm._v(" Men prepared the bread sacrifice in the cultic context, while reference is made to a “Bakers’ Street” (masculine plural) in Jerusalem in the sixth century BCE. Otherwise the Bible indicates that women were responsible for grinding grain and baking bread for the family; the Hebrew Bible offers no other information about gender distinctions in food production."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en57",
      "id": "en57r"
    }
  }, [_vm._v("9")])]), _vm._v(" Biblical texts and archeological and iconographic evidence indicate that women worked cooperatively in food production. Large ovens, fueled by wood or animal dung, were often situated in courtyards between houses so that baking became a cooperative enterprise and opportunity "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p36"
    }
  }), _vm._v("for social interaction."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en58",
      "id": "en58r"
    }
  }, [_vm._v("10")])]), _vm._v(" Often several sets of grinding stones are found in a single dwelling unit, indicating that two or three women were grinding grain at the same time.")]), _vm._v(" "), _c('p', [_vm._v("The second component of the Mediterranean triad was olive oil. Olives were too bitter to be eaten raw, and techniques that make the olive edible were not known until the postbiblical era. Oil was produced by crushing olives by foot, by pounding them with a mortar, or on a larger scale with a mechanism called a beam press. Oil was most important as an ingredient of bread and as a dip for bread and other foods. It served an important function in religious life as an additive to sacrifices and as the fuel for the menorah or candelabra, which illuminated the Tent of Meeting."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en59",
      "id": "en59r"
    }
  }, [_vm._v("11")])]), _vm._v(" Also, it was used for anointing priests and kings, an indication that the person anointed was God’s chosen."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en60",
      "id": "en60r"
    }
  }, [_vm._v("12")])]), _vm._v(" In a profane context, applying oil to one’s head was an indication of joy."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en61",
      "id": "en61r"
    }
  }, [_vm._v("13")])])]), _vm._v(" "), _c('p', [_vm._v("The third component of the triad is wine. With its hot, dry summers and wet, cool winters, the Israelite climate is ideal for viticulture. Grapes required little water for growth and gave juice without tapping into limited water resources."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en62",
      "id": "en62r"
    }
  }, [_vm._v("14")])]), _vm._v(" Grapes were consumed fresh or dried, as juice or vinegar, but most importantly as wine. Because fermentation occurred quickly in Israel’s warm climate, wine was more available than grape juice. It may have been occasionally mixed with water and other ingredients. Wine was the principal drink of ancient Israel, although water supplied hydration to travelers in the wilderness and workers in the fields."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en63",
      "id": "en63r"
    }
  }, [_vm._v("15")])]), _vm._v(" The importance of grapes appears in the biblical narrative, when Moses sent out spies to reconnoiter the land and they brought back one cluster of grapes too heavy for one man to carry."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en64",
      "id": "en64r"
    }
  }, [_vm._v("16")])]), _vm._v(" Wine’s status as a staple of the Israelite diet is evident in the book of Lamentations, a poetic response to the siege and destruction of Jerusalem in 586 BCE: young children “keep asking their mothers, ‘Where is bread and wine?’”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en65",
      "id": "en65r"
    }
  }, [_vm._v("17")])]), _vm._v(" An Egyptian text expresses the perception that ancient Israel “had more wine than water.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en66",
      "id": "en66r"
    }
  }, [_vm._v("18")])]), _vm._v(" Wine also served a religious function as a liquid sacrifice and as a central component of sacred feasts."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en67",
      "id": "en67r"
    }
  }, [_vm._v("19")])]), _vm._v(" Many biblical texts associate wine with joyful occasions."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en68",
      "id": "en68r"
    }
  }, [_vm._v("20")])]), _vm._v(" Despite the importance of wine and its association with happiness, however, many biblical texts warn of the dangers of drunkenness."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en69",
      "id": "en69r"
    }
  }, [_vm._v("21")])])]), _vm._v(" "), _c('p', [_vm._v("Ancient Israelites found evidence of God’s blessings in other foods as well. The Land of Israel is often called a “land flowing with milk and "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p37"
    }
  }), _vm._v("honey.” Honey in this context, the Hebrew "), _c('i', [_vm._v("devash")]), _vm._v(", refers mostly to date syrup and less often to bees’ honey."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en70",
      "id": "en70r"
    }
  }, [_vm._v("22")])]), _vm._v(" Goats, and not cattle or sheep, were the animals associated with milk. Milk and other dairy products have obviously positive associations in the Bible. Fresh goat milk is not available year round in traditional societies because goats birth on a seasonal basis during December and January, wean their young in March and April, and only after that are milked solely for human consumption through the summer months. Drinking milk is mentioned only in Judges 4:19, and so it appears that more commonly, milk would be consumed in other forms, such as soured milk, loose yogurt, or cream and butter from cow’s milk. In the warm Mediterranean climate, milk quickly sours and has to be processed or preserved for those months when nanny goats gave no milk. Soured milk could be turned into cheese by allowing the water to drain and evaporate, with salt added during the process."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en71",
      "id": "en71r"
    }
  }, [_vm._v("23")])]), _vm._v(" As for the abundant fertility of the Land of Israel, Deuteronomy 8:7 references seven fruits of the land and idealizes its blessings: “For the Lord your God is bringing you into a good land, a land with streams and springs and fountains issuing from plain and hill; a land of wheat and barley, of vines, figs, and pomegranates, a land of olive trees and honey; a land where you may eat food without stint, where you will lack nothing; a land whose rocks are iron and from whose hills you can mine copper.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en72",
      "id": "en72r"
    }
  }, [_vm._v("24")])]), _vm._v(" Modern scholars are skeptical that this lavish description accurately represents the actual Land of Israel. Rather, the Bible’s praise serves a theological purpose: God would clearly not promise or reward a less-than-perfect land to His covenant people.")]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0202"
    }
  }, [_vm._v("Foods as Daily and Holiday Offerings")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("It is not surprising, then, that ancient Israel’s cult—that is, its official system of rituals as reflected in the Hebrew Bible—gives food a central role. Sacrifice is the primary mode of worship. Whether the explicit purpose of the sacrifice is to express gratitude, enact expiation, request interventions, or other reasons, it demands the donation of a variety of foods. Scholars of religion have suggested many different interpretations of sacrifice, particularly animal sacrifice, and various functions of this richly symbolic act can coexist. Jacob Milgrom leans toward seeing animal sacrifice as a gift to induce the assistance of the deity and at the same "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p38"
    }
  }), _vm._v("time views the dedication of the animal as a way to rid the giver of the guilt resulting from its death."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en73",
      "id": "en73r"
    }
  }, [_vm._v("25")])]), _vm._v(" In this sense, it is the sacrifice (the ritual dedication of the animal’s blood and fat at the altar and the setting aside of portions for others) that enables the giver and his family to consume the meat in celebration without culpability."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en74",
      "id": "en74r"
    }
  }, [_vm._v("26")])]), _vm._v(" Further, since food abundance is YHWH’s gift for obedience to Israel’s covenant, recipients—or seekers—of His bounty bring food offerings to the local or central cult site and its staff. The sacrifice of livestock was accompanied by offerings of grain, oil, and wine, so a meal devoted to God resembled the ideal human meal."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en75",
      "id": "en75r"
    }
  }, [_vm._v("27")])]), _vm._v(" In addition, Israelites were expected to give tithes (one-tenth), firstfruits, and firstlings of all produce and livestock. Sources differ as to the recipients of these contributions. While Deuteronomy allows the giver and his family to joyfully consume the offerings in the holy place, presumably Jerusalem, Leviticus and Numbers demand that the Levites (the tribe designated to serve at the Sanctuary) receive the donations as compensation because they received no landed property in Israel, and the poor would receive some as well."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en76",
      "id": "en76r"
    }
  }, [_vm._v("28")])]), _vm._v(" Farmers participated in a ritual when they brought their firstfruits for acceptance at the altar: they declared God’s great acts of salvation in Israelite history and offered thanks for the gift of a land “flowing with milk and honey.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en77",
      "id": "en77r"
    }
  }, [_vm._v("29")])])]), _vm._v(" "), _c('p', [_vm._v("Although we cannot be sure to what extent altar or Temple rituals were mirrored by domestic customs, it is likely that Israelites celebrated Sabbaths, new moons, and other festivals with special meals and familial and communal gatherings."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en78",
      "id": "en78r"
    }
  }, [_vm._v("30")])]), _vm._v(" Israelites were obligated to participate in three pilgrimage festivals each year so that celebrations would have taken place at the local or national altar."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en79",
      "id": "en79r"
    }
  }, [_vm._v("31")])]), _vm._v(" The Pesach or Passover sacrifice was entirely consumed by the family (or extended family) unit. Passover was also commemorated by a complete ban on leavening for seven days, and Israelites were enjoined to eat unleavened bread, matzah, for seven days to commemorate the Exodus from Egypt. Following Passover, another ritual that had reverberations in the home was the waving of the omer, the first sheaf of the spring barley harvest. Until this offering was made, no part of the new grain crop could be eaten in the home or elsewhere. Seven weeks after the omer offering, a second festival—today known as the holiday of Shavuot—was celebrated with its prescribed sacrificial offerings. The third festival, Sukkot, occurred at harvest time, at the end of the summer, and biblical texts describe it as "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p39"
    }
  }), _vm._v("being marked by feasting in huts erected in the harvested fields as well as a pilgrimage including sacrificial offerings."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en80",
      "id": "en80r"
    }
  }, [_vm._v("32")])]), _vm._v(" In the postbiblical and post-Temple era, Jewish texts describe at great length the Sabbath and holiday practices that included home-based feasting and fasting.")]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0203"
    }
  }, [_vm._v("Eating Animals")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("For the Torah, the eating of land animals is more circumscribed by rules than any other food. The Bible equates humans and animals in a surprising array of passages: land animals are created from the soil on the sixth day of creation as humans are;"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en81",
      "id": "en81r"
    }
  }, [_vm._v("33")])]), _vm._v(" they, like humankind, are God’s covenant partners in the aftermath of the flood;"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en82",
      "id": "en82r"
    }
  }, [_vm._v("34")])]), _vm._v(" animals are subject to the death penalty for bestiality and homicide;"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en83",
      "id": "en83r"
    }
  }, [_vm._v("35")])]), _vm._v(" and the illicit spilling of animal blood is likened to human bloodshed."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en84",
      "id": "en84r"
    }
  }, [_vm._v("36")])]), _vm._v(" This tendency to liken land animals to people is in part a function of their important role in daily life. Furthermore, both land animals and humans have copious amounts of blood, which in the Bible is the substance of life and thus forbidden for human consumption. Based on the Bible’s testimony, we can assume that virtually all meat eating in the preceding centuries of Israelite history started as a sacrificial ritual at the local holy sites, and this included a specific way of treating the animal blood. Eating the meat of domestic animals without the ceremony at the altar was, in some biblical passages, sanctioned only later in the biblical era after King Josiah of Judah initiated a reform that eliminated all altar sites except for the central Jerusalem Temple.")]), _vm._v(" "), _c('p', [_vm._v("A fundamental starting point for understanding the Torah’s restrictions on the eating of animals is found in the story of creation, when God expresses the intention that humankind consumes seed-bearing plants and fruit, while animals eat green plants for food. Humankind is entitled to “rule” over animals (a reference to their domestication and/or the use of their by-products) but not to kill them. According to the Torah’s narrative, after God responds to violence on the part of both humans and animals by flooding the world, He ushers in a new order with legislation intended for humanity as a whole: human blood must not be shed, and although people are now permitted to consume an animal’s flesh, they must not consume its blood. The life fluid of the animal must be drained into the earth."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en85",
      "id": "en85r"
    }
  }, [_vm._v("37")])])]), _vm._v(" "), _c('p', [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p40"
    }
  }), _vm._v("Since the biblical narrative teaches that eating meat is only allowed after humankind has proven that their nature is irredeemably evil, many modern interpreters conclude that eating meat is a concession to humankind’s violent nature and therefore vegetarianism is an ethical ideal (a topic explored in some detail in the last chapter of this volume)."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en86",
      "id": "en86r"
    }
  }, [_vm._v("38")])]), _vm._v(" There is no explicit statement of such an ideal, however, or evidence that many Israelites voluntarily adopted vegetarianism. Yet the consensus of modern scholars is that meat was an infrequent item in the Israelite diet because they assume that the farmer would be reluctant to deplete livestock, which gave wool or milk or the muscle to pull the plow. They argue that meat eating would occur at the annual clan celebration or pilgrimage festival or for the fulfillment of a vow."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en87",
      "id": "en87r"
    }
  }, [_vm._v("39")])]), _vm._v(" Further, they suggest that meat was only consumed when there were enough people present to eat the meat within a few days; they suggest that this was due to the lack of refrigeration and that perhaps the commandment to discard the meat of a sacrifice after the second day was in reference to the meat’s growing unpleasantness."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en88",
      "id": "en88r"
    }
  }, [_vm._v("40")])]), _vm._v(" The archeological record, however, and information about livestock utilization suggest more frequent meat consumption. It may be that primarily the elite—priests and royalty and their circles—consumed meat, as reflected in texts that suggest considerable donations of livestock to the priesthood. The abundant supply of meat available to royalty is reflected in the menu at King Solomon’s table: “ten fattened oxen, twenty pasture-fed oxen, and one hundred sheep and goats besides deer and gazelles, roebucks and fattened geese.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en89",
      "id": "en89r"
    }
  }, [_vm._v("41")])]), _vm._v(" Even if meat eating was not a rare event, it may not have been a staple for most Israelites’ diet.")]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0204"
    }
  }, [_vm._v("Dietary Laws and Animals")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("The Hebrew Bible includes food laws, but these are not presented systematically as a daily food regimen detailing food choices, preparation, and food mixtures—such a system, later known as "), _c('i', [_vm._v("kashrut")]), _vm._v(", was developed in the postbiblical rabbinic, medieval, and modern eras (described further in this volume’s upcoming chapters). The biblical dietary laws focus primarily on permitted and prohibited foods. Obeying these was considered essential to being God’s holy nation. Because they focus so "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p41"
    }
  }), _vm._v("much on animals, these laws indicate not only food choices but also beliefs about how animals should be treated.")]), _vm._v(" "), _c('p', [_vm._v("The Torah offers two chapters of detailed legislation regarding the consumption of animals, but no laws restrict the eating of some plants as opposed to others."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en90",
      "id": "en90r"
    }
  }, [_vm._v("42")])]), _vm._v(" Leviticus 11 and Deuteronomy 14 differentiate between permitted and prohibited animals of all categories: land animals, birds and insects, sea animals, and amphibians. These restrictions are accompanied by God’s demand that Israel be “holy” and dedicated to Him. Permitted mammals have “true hooves, with clefts through the hooves, and that chew the cud.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en91",
      "id": "en91r"
    }
  }, [_vm._v("43")])]), _vm._v(" The divided hoof of permitted animals such as goats and cows can be contrasted with the singular or solid toe of a horse or donkey or the paw of a lion or dog. Animals that chew the cud are ruminants, which refer to herbivores equipped with a four-chambered stomach; after the animal chews and regurgitates plant material several times, the nutrients can be absorbed through digestion. These criteria also apply to wild animals, so deer, gazelle, antelope, and ibex are permitted. Pigs are prohibited because while they do have split hooves, they are not ruminants.")]), _vm._v(" "), _c('p', [_vm._v("The Torah does not explain its choice of permitted animals. Over the centuries, many theories have been proposed, but just four will be mentioned here as the most commonly suggested as indicative of ancient perspectives. The first is the health/hygienic theory, which assumes that the forbidden animals are carriers of disease or have a deleterious effect on health in general. However, no such health association is alluded to in the Bible, and there is no evidence to support a health-related theory for the dietary restrictions."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en92",
      "id": "en92r"
    }
  }, [_vm._v("44")])]), _vm._v(" The second is the view that the criteria are arbitrary and have no intrinsic value: they are decrees meant to instill self-restraint and demonstrate obedience. This is arguably implied by the Torah itself, which offers no explanation other than conformity with God’s will. A third family of theories suggests that the permitted animals, or their unique characteristics, are symbolic of desirable traits. An ancient Jewish philosopher named Philo Judaeus writes that the split hoof represents the ability to distinguish between right and wrong and chewing the cud symbolizes a person’s mental rumination—that is, mastery of knowledge through “continued exercise of memory.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en93",
      "id": "en93r"
    }
  }, [_vm._v("45")])]), _vm._v(" Other scholars look at the common biblical metaphors of Israel as sheep or "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p42"
    }
  }), _vm._v("flock animals and conclude the permitted animals represent qualities that should be emulated. Israel “was to be like that which she ate”—that is, loyal and peace loving."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en94",
      "id": "en94r"
    }
  }, [_vm._v("46")])])]), _vm._v(" "), _c('p', [_vm._v("The fourth family of theories can be termed "), _c('i', [_vm._v("religioethical")]), _vm._v(". Building on the scholarship of Milgrom, who ties God’s demand for holiness with ethics and reverence for life, Israel’s dietary laws are viewed as a means to limit access to the animal kingdom and reduce the Israelite’s choice of flesh to just a few animals that are the main domestic species kept by pastoralists."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en95",
      "id": "en95r"
    }
  }, [_vm._v("47")])]), _vm._v(" This ethical theory is compatible with ecological pragmatism in that a pig is raised solely for meat and is therefore an economic drain, while other livestock, such as cattle, sheep, or goats, had other useful functions."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en96",
      "id": "en96r"
    }
  }, [_vm._v("48")])]), _vm._v(" Another variation of the religioethical theory is expressed by Baruch Levine. He points to the Torah’s blood prohibition as an underlying principle of the prohibited species and suggests that permitting only herbivorous ruminants will ensure that foods forbidden to the Israelites will not have been consumed by the animals they were going to eat. Carnivorous birds are likewise forbidden, as are crustaceans (crabs or lobster) with their claws (as opposed to permitted “peaceful” fish with their fins). Levine writes that method of locomotion is the second factor. Only an animal with truly cleft hooves was regarded as domesticated, but “paws were bestial and so forbidden.” Regarding the pig, he writes that there is nothing in the dietary codes or the Bible to indicate that it was singled out as an abomination; rather, it is mentioned in Leviticus 11 because it is a borderline case, possessing a fully cleft hoof without chewing its cud. Like Milgrom, Levine points to the vegetarian ideal of Genesis as the underlying rationale for the dietary laws. He regards blood, the substance of life, as the principle element in the dietary laws: humankind may not consume blood, but Israel may not even eat animals that eat blood."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en97",
      "id": "en97r"
    }
  }, [_vm._v("49")])]), _vm._v(" Another example of religioethical interpretation is found in Daniel Weiss’s chapter in this volume.")]), _vm._v(" "), _c('p', [_vm._v("Because the consumption of blood was forbidden, the means of slaughter was dictated by the concern to ensure the easy removal of blood from the meat. A specific procedure intimated by the Hebrew verb "), _c('i', [_vm._v("shahat")]), _vm._v("—the slitting of the animal’s throat—permitted the blood from the major blood vessels to fully drain."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en98",
      "id": "en98r"
    }
  }, [_vm._v("50")])]), _vm._v(" If the animal was sacrificed at an altar, the blood would be thrown against its walls, but if the animal was slaughtered in a nonsacrificial context or through hunting, the "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p43"
    }
  }), _vm._v("blood would be poured on the ground and covered with dirt."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en99",
      "id": "en99r"
    }
  }, [_vm._v("51")])]), _vm._v(" The same applied to birds."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en100",
      "id": "en100r"
    }
  }, [_vm._v("52")])]), _vm._v(" An extension of this law against consuming blood was the prohibition of eating the carcass of a clean animal that was not slaughtered—that is, animals that died of disease or old age or animals torn by other animals. In Exodus 22:30, God decrees, “You shall be a people holy to Me: you must not eat flesh torn by beasts in the field; you shall cast it to the dogs.” Based on the same concern for holiness, Deuteronomy 14:21 adds that any animal that has died a natural death must be transferred to and consumed by a non-Israelite. (Later Jewish kosher slaughter preserves and expands upon these principles.)")]), _vm._v(" "), _c('p', [_vm._v("Although hunters and their techniques and tools are mentioned occasionally in the Hebrew Bible, and it is permitted to eat certain animals killed by hunting as long as their blood is drained, it is unlikely that hunting contributed much to Israelite subsistence."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en101",
      "id": "en101r"
    }
  }, [_vm._v("53")])]), _vm._v(" Biblical texts mention hunting birds with nets and traps, and the references to the use of pits suggest that Israelites sought bigger game too."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en102",
      "id": "en102r"
    }
  }, [_vm._v("54")])]), _vm._v(" Some animals that pose a threat to people and their flocks, such as lions or bears, may have been hunted but not eaten."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en103",
      "id": "en103r"
    }
  }, [_vm._v("55")])]), _vm._v(" Wild animals that were cloven-hooved ruminants, such as deer or gazelles, could be eaten as long their blood was poured out on the earth and covered with soil in accordance with the Torah’s blood prohibition.")]), _vm._v(" "), _c('p', [_vm._v("While the blood prohibition applies to all animals, the prohibition of "), _c('i', [_vm._v("helev")]), _vm._v(", the fat attached to the stomach and intestines, applies only to animals that were sacrificed."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en104",
      "id": "en104r"
    }
  }, [_vm._v("56")])]), _vm._v(" Genesis 32 records the Israelite taboo of the sciatic nerve. This prohibition is explained by the story of Jacob wrestling with an angel who wrenches his hip at its socket: “That is why the children of Israel to this day do not eat the thigh muscle that is on the socket of the hip, since Jacob’s hip socket was wrenched at the thigh muscle” (v. 33). Nowhere else in the Hebrew Bible is this prohibition mentioned, and archeological evidence cannot affirm whether it was obeyed. (Later Jewish law mandates the removal of both the "), _c('i', [_vm._v("helev")]), _vm._v(" and the sciatic nerve from animals slaughtered for food.)")]), _vm._v(" "), _c('p', [_vm._v("Finally, a notable element of the Bible’s dietary laws is the thrice-repeated commandment “You shall not boil a kid in its mother’s milk.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en105",
      "id": "en105r"
    }
  }, [_vm._v("57")])]), _vm._v(" In its simple biblical sense, this enigmatic rule prohibits the cooking of a young goat in its own mother’s milk. The goat is mentioned here either because it was the main source of milk in ancient Israel, because "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p44"
    }
  }), _vm._v("it was the most commonly owned type of cattle, or because goat meat is most in need of tenderizing through cooking in milk."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en106",
      "id": "en106r"
    }
  }, [_vm._v("58")])]), _vm._v(" It is possible that the clause intends to include sheep and large cattle, but evidence for the generalization of this prohibition to a complete ban of combining dairy and meat appears only in the later rabbinic period. (This becomes a major principle of "), _c('i', [_vm._v("kashrut")]), _vm._v(", the Jewish dietary laws, as the next chapter will show.) While the Torah offers no reason for the prohibition, some later commentators suggest that it is grounded in sensitivity to animal life, so that an animal’s young not be destroyed by the fluid meant to sustain it."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en107",
      "id": "en107r"
    }
  }, [_vm._v("59")])]), _vm._v(" We have no evidence that biblical people honored this dietary restriction.")]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0205"
    }
  }, [_vm._v("Ecological Pragmatism and Prohibited Animals")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("The Torah’s disqualification of whole classes of animals from Israel’s diet must be considered from an ecologically pragmatic approach—that is, whether it was a useful response to environmental considerations. The most common domestic animals in ancient Israel and the wider Near East were goat, sheep, and bovine, and so from the ecologically pragmatic approach, the permissibility of cloven-hooved ruminants makes sense. Yet from that same perspective, the prohibition of the pig, which was raised all over the ancient Near East, cannot be explained. Pig and dog bones are present in ancient Egyptian and Syrian animal remains, even though both animals were urban scavengers and sometimes the object of disgust."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en108",
      "id": "en108r"
    }
  }, [_vm._v("60")])]), _vm._v(" While pig rearing was practiced at sites along Israel’s coast that were settled by the Philistines, a people of Aegean origin, pig bones are notably absent farther east in the Iron Age (1200–1000 BCE) settlements where the people of Israel emerged."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en109",
      "id": "en109r"
    }
  }, [_vm._v("61")])])]), _vm._v(" "), _c('p', [_vm._v("In contrast to the archeological record, the textual evidence for the observance of the food taboos is weak for the early period of Israelite society. The Hebrew Bible refers to Israelites who refused unclean food without explicitly mentioning prohibited animals."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en110",
      "id": "en110r"
    }
  }, [_vm._v("62")])]), _vm._v(" Later sources are more explicit: for example, Isaiah 66:3 and 66:17, passages that are considered to be recorded after exile of 586 BCE, condemn the sacrifice of dogs, mice, and swine—indicating both great respect for and rejection of the taboos. Hellenistic Jewish literature (notably 2 Maccabees 6–7), however, tells of Jews who become martyrs rather than eat pork.")]), _vm._v(" "), _c('p', [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p45"
    }
  }), _vm._v("Some scholars suggest that this abhorrence of pork, which was carried into later Jewish foodways, was the result of the dietary laws mentioned in the Pentateuch. Alternatively (or at the same time), some suggest it may be the consequence of more complex cultural considerations, such as the contempt for urban living—of which pigs were characteristic—held by pastoralist Israelites or those who recalled their past as desert nomads, or perhaps it reflects the antagonistic relations of Israel with the nearby foreigners who ate pig. Another suggestion is that the problem with pig rearing is that it demands too much water from Israel’s dry climate. Yet there could have been ecological and economic benefits for pigs in Israel’s village and city life. Pigs are omnivores and therefore eat garbage and human feces, turning both into edible meat. (They play this role in garbage collection and waste disposal, as an “edible sanitation service,” in both ancient and modern Egypt and in many other geographical and historical contexts."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en111",
      "id": "en111r"
    }
  }, [_vm._v("63")])]), _vm._v(") Pigs have much larger litters than cows, sheep, or goat and a much faster growth rate, and they can be exploited for their lard, skin, mineral-rich waste, and bristles."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en112",
      "id": "en112r"
    }
  }, [_vm._v("64")])]), _vm._v(" In sum, Israel’s pig taboo, whatever its basis or bases, was unique in the ancient world and resists an explanation based on economic and ecological considerations."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en113",
      "id": "en113r"
    }
  }, [_vm._v("65")])])]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0206"
    }
  }, [_vm._v("Fish, Birds, Eggs, and Insects as Food or Taboo")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("The consumption of marine life is included in the Torah’s laws on animals: only those creatures that have fins and scales may be eaten. The prohibition excludes from Israel’s diet aquatic animals such as crustaceans or mollusks."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en114",
      "id": "en114r"
    }
  }, [_vm._v("66")])]), _vm._v(" No specific species are mentioned; indeed, eating fish is mentioned only four times in the Hebrew Bible, and the only species of fish named is a mythical sea monster."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en115",
      "id": "en115r"
    }
  }, [_vm._v("67")])]), _vm._v(" Israel’s geography would suggest that fish were seldom eaten in ancient Israel because the heartland of its population lay in its hilly interior, which had few perennial rivers, natural harbors, or lakes. Fish go unmentioned among the “clean” animals suitable for sacrifice, perhaps because they do not have copious amounts of blood, the substance used in sacrificial ritual. On the other hand, other data indicate the consumption of fish and familiarity with fishing in ancient Israel. The specialized gear used for fishing, including hooks and various kinds of nets, is mentioned in the Hebrew Bible,"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en116",
      "id": "en116r"
    }
  }, [_vm._v("68")])]), _vm._v(" "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p46"
    }
  }), _vm._v("and archeological research, specifically the science of paleozoology, has uncovered significant consumption of fish during the Iron Age."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en117",
      "id": "en117r"
    }
  }, [_vm._v("69")])]), _vm._v(" The fish consumed probably came from nearby freshwater sources, such as the Yarkon and Jordan Rivers and the Sea of Galilee. Saltwater fish would have originated in the Mediterranean and perhaps the Red Sea. Nathan MacDonald suggests that because of the long distance involved, consumption of fish would have been restricted to elites in urban areas and to those who lived along the rivers."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en118",
      "id": "en118r"
    }
  }, [_vm._v("70")])]), _vm._v(" In this case, the findings of archeology have modified assumptions made on the basis of Israel’s geography and evidence from the Hebrew Bible.")]), _vm._v(" "), _c('p', [_vm._v("The Torah’s legislation shows a great familiarity with species of birds, in contrast with its references to fish. While those permitted are not named, Leviticus 11 and Deuteronomy 14 list twenty-one species of forbidden birds. These include eagles, vultures, falcons, ravens, crows, various kinds of owls, hawks, ospreys, storks, herons, hoopoes, and bats (a mammal, or course, but Leviticus categorizes animals by means of locomotion). The common denominator regarding forbidden birds is their identity as raptors—that is, consumers of carrion or live prey."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en119",
      "id": "en119r"
    }
  }, [_vm._v("71")])]), _vm._v(" Clearly these birds were available as food, but religious and cultural factors prevented them from being considered acceptable. The frequent mention of fowling (hunting wild birds with nets) indicates that permitted wild birds were consumed."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en120",
      "id": "en120r"
    }
  }, [_vm._v("72")])]), _vm._v(" Some birds were probably domesticated and kept in cages, and their eggs were probably eaten. The eggs or fledglings of wild birds were also eaten, and the Israelite was required to shoo away the mother bird before taking these, a biblical law enshrined in later Jewish practice."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en121",
      "id": "en121r"
    }
  }, [_vm._v("73")])]), _vm._v(" Leviticus mentions turtledoves and pigeons as sacrifices acceptable from poor people, which suggests that these birds were inexpensive and plentiful."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en122",
      "id": "en122r"
    }
  }, [_vm._v("74")])]), _vm._v(" One issue of contention among scholars is whether ancient Israelites kept the domestic chicken: the archeological evidence is ambiguous."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en123",
      "id": "en123r"
    }
  }, [_vm._v("75")])])]), _vm._v(" "), _c('p', [_vm._v("While Westerners generally view eating insects with disgust, Israel and other ancient Near Easterners had a more tolerant attitude to consuming locusts, crickets, and grasshoppers. Leviticus 11:20 declares, “All winged swarming things that walk on fours shall be an abomination to you,” but then enumerates four species of locusts that are permissible. Within the Hebrew Bible, no explicit mention is made again of the consumption of these insects, although in the New Testament, the Jewish "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p47"
    }
  }), _vm._v("ascetic John the Baptizer ate locusts and wild honey."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en124",
      "id": "en124r"
    }
  }, [_vm._v("76")])]), _vm._v(" Milgrom suggests that locusts and grasshoppers were made exceptions to the blanket prohibition on insects because this practice recalls Israel’s past as pastoral nomads, who feast on locusts to this day."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en125",
      "id": "en125r"
    }
  }, [_vm._v("77")])]), _vm._v(" Locusts are known otherwise in the Hebrew Bible for their catastrophic invasions. It is possible that the Torah’s permission to consume locusts is ironically a consequence of these periodic calamities, as having no other source of food, the people collected and ate the invading insects!")]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0207"
    }
  }, [_vm._v("Other Foods: Vegetables, Pulses, Fruit, Spices, and Condiments")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("Ancient Israelites probably consumed various kinds of fruits and vegetables in addition to the Mediterranean triad. However, archeological evidence for their consumption is lacking because they leave no trace, unlike bones for the consumption of meat or olive presses for oil. Legumes and pulses, which include lentils, broad beans, chickpeas, and vetch, were valuable supplements to their diet. They served as a source of protein in the absence of meat and could be easily dried and stored for times of food shortage."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en126",
      "id": "en126r"
    }
  }, [_vm._v("78")])])]), _vm._v(" "), _c('p', [_vm._v("The Hebrew Bible offers contradictory attitudes to eating vegetables. 1 Kings 21 notes that the king of Israel, Ahab, desired land for a vegetable garden, and Numbers 11:5 records that the Israelites longed for the cucumbers and leeks they ate in Egypt. Other texts suggest that vegetables were generally undesirable or considered unhealthy to ancient Israelites."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en127",
      "id": "en127r"
    }
  }, [_vm._v("79")])]), _vm._v(" Proverbs 15:17 advises, “Better a meal of vegetables where there is love, than a fattened ox where there is hate.” The narrative in Daniel 1 suggests that eating a vegetarian diet normally would make one look sickly. Vegetables, therefore, may have been a desirable addition to a meal centered on animal-based foods (meat, eggs, fish), but a solely vegetarian diet was considered undesirable. The need for nutrition in a limited environment means that eating meat, which is denser in nutrients, fat, and calories, would be favored for providing more effectively for periods when food was scarce. In addition, the association of meat eating with celebration is in opposition with the idea that there is a general biblical religious and ethical vegetarian ideal."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en128",
      "id": "en128r"
    }
  }, [_vm._v("80")])])]), _vm._v(" "), _c('p', [_vm._v("Fruit plays a prominent role in the Hebrew Bible. In Genesis 2, it constitutes the first food of humankind in the Garden of Eden. Fruit was "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p48"
    }
  }), _vm._v("associated with sensual pleasure; fruits are mentioned twenty-five times in the eight chapters of the Song of Songs, a corpus of erotic love poetry. The importance of fruit trees is suggested by Deuteronomy 20:19–20, which prohibits the Israelite from cutting down fruit trees when laying siege to an enemy city. Deuteronomy 8:8 praises the Land of Israel for its plentiful grapes, figs, pomegranates, olives, and date syrup ("), _c('i', [_vm._v("devash")]), _vm._v(")."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en129",
      "id": "en129r"
    }
  }, [_vm._v("81")])]), _vm._v(" Elsewhere the Hebrew Bible mentions the "), _c('i', [_vm._v("tapu’ah")]), _vm._v(" (usually identified as an apple), the sycamore fig, the mandrake, and the watermelon—which is mentioned only in the context of Egypt."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en130",
      "id": "en130r"
    }
  }, [_vm._v("82")])]), _vm._v(" Dates, grapes, and figs could be eaten fresh but also dried and pressed into cakes. Grapevines and fig trees are paired to convey stereotypical Israelite holdings: “All the days of Solomon, Judah and Israel from Dan to Beersheba dwelt in safety, everyone under his own vine and under his own fig tree.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en131",
      "id": "en131r"
    }
  }, [_vm._v("83")])]), _vm._v(" Leviticus 23:40 commands the Israelite to utilize the fruit of the "), _c('i', [_vm._v("hadar")]), _vm._v(" tree in the ritual for the festival of Tabernacles, and later Jewish tradition identified it as an "), _c('i', [_vm._v("‘etrog")]), _vm._v(" or citron."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en132",
      "id": "en132r"
    }
  }, [_vm._v("84")])]), _vm._v(" Only two kinds of nuts, pistachios and almonds, appear in the Hebrew Bible as specialty items, so these were probably not significant for the average Israelite."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en133",
      "id": "en133r"
    }
  }, [_vm._v("85")])])]), _vm._v(" "), _c('p', [_vm._v("Salt was important for flavoring and preserving food. The book of Job asks, “Can what is tasteless be eaten without salt?”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en134",
      "id": "en134r"
    }
  }, [_vm._v("86")])]), _vm._v(" It was an additive to the grain sacrifice (Leviticus 2:13), and because salt was a preservative, it symbolized permanence in the Hebrew Bible; God’s covenant with the priesthood is called a “covenant of salt” (Numbers 18:19). Salt was obtained either through mining or via the evaporation of salt water from the Dead Sea or the Mediterranean. Coriander, cumin, and saffron are mentioned in the Hebrew Bible."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en135",
      "id": "en135r"
    }
  }, [_vm._v("87")])]), _vm._v(" Numbers 11:5 records that Israelites pined for Egyptian onion and garlic, two vegetables that enhance the flavor of other foods, but these are never mentioned as readily available. Otherwise, the Hebrew Bible enumerates several other spices, but these were expensive and exotic ingredients for cosmetics, incense, and perfumed ointments rather than food condiments.")]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0208"
    }
  }, [_vm._v("Food Scarcity and Charity")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("As noted earlier, disparities must have existed between the nutritional resources of various groups in ancient Israel depending on geography and social class."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en136",
      "id": "en136r"
    }
  }, [_vm._v("88")])]), _vm._v(" Those who lived along trade routes enjoyed fish or "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p49"
    }
  }), _vm._v("nuts or spices more often than those more distant. Those who raised livestock consumed a greater quantity of milk products than grain farmers. The wealthy and families of the priestly and royal classes would have had greater access to meat.")]), _vm._v(" "), _c('p', [_vm._v("Food scarcity in ancient Israel may be clarified by the distinctions made by Peter Garnsey, who distinguishes between "), _c('i', [_vm._v("famine")]), _vm._v(", a critical shortage of essential foods leading to increased mortality; "), _c('i', [_vm._v("food shortage")]), _vm._v(", a short-term reduction of available food supplies leading to rising prices, hunger, and increased mortality; and "), _c('i', [_vm._v("chronic malnutrition")]), _vm._v(", which is long-term food deprivation."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en137",
      "id": "en137r"
    }
  }, [_vm._v("89")])]), _vm._v(" Inhabitants of ancient Israel probably experienced famine once or twice over a lifetime and food shortages more frequently. The poor or landless may have experienced chronic malnutrition for much of their lives, weakening their resistance to disease and bringing early death. MacDonald extends the prevalence of chronic malnutrition to the Iron Age population of Israel in general and suggests that most suffered from “an inadequate diet, poor health, and low life expectancy.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en138",
      "id": "en138r"
    }
  }, [_vm._v("90")])]), _vm._v(" Drought and siege would have only increased the desperation of the poor. Israel, reliant upon rainfall, was subject to years with little or no rain, and a common motif in the Hebrew Bible is departure from the land of Israel in search of food."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en139",
      "id": "en139r"
    }
  }, [_vm._v("91")])]), _vm._v(" Passages concerning prolonged siege or drought mention cannibalism; however, such reports should be treated with skepticism."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en140",
      "id": "en140r"
    }
  }, [_vm._v("92")])])]), _vm._v(" "), _c('p', [_vm._v("The Torah offers legislation intended to ameliorate the precarious food situation of the poor. For example, Leviticus 19:9–10 and Deuteronomy 24:19–21 command the Israelite landowner to leave behind various portions of his field and vineyard for the poor, and this practice is mentioned in the case of Ruth, a widow who enters the field of a wealthy landowner to pick up the grain stalks left behind."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en141",
      "id": "en141r"
    }
  }, [_vm._v("93")])]), _vm._v(" The Torah also commands that the necessary tools for processing grain, the millstones, should not be taken as collateral for a loan and that employers pay day laborers before sundown and not delay payment at the employer’s convenience."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en142",
      "id": "en142r"
    }
  }, [_vm._v("94")])]), _vm._v(" Admittedly, in a time of famine or food shortages, these measures may not have helped the poor and landless. It is impossible to know if these laws were widely implemented, but they were carried into later Jewish religious law.")]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0209"
    }
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p50"
    }
  }), _vm._v("Food Laws and Social Boundaries")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("Food taboos create social boundaries. The function of dietary laws to separate Israelites/Jews from gentiles is apparent only in the later biblical era. In Exodus 34:15, Israel as a nation is warned against making covenants with the inhabitants of the Land of Canaan and the eating of their sacrifices, with the result that Israelite sons will marry Canaanite daughters and worship their gods. Here the problem is not the substance of the food itself but the socializing associated with festive meals. Numbers 25:1–3 tells of an incident in which eating sacrifices and social intercourse induced Israelites to worship other gods. The food prohibitions described earlier in this chapter are associated with Israel’s sanctification and the avoidance of impurity. The goal of separating Israel from other nations is mentioned, but it is secondary to the objective of maintaining Israel’s status as a “holy people.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en143",
      "id": "en143r"
    }
  }, [_vm._v("95")])]), _vm._v(" David Kraemer notes that on a daily basis, the food prohibitions would have barely distinguished the Israelites from other ethnic groups because the staples common to all groups consisted of foods not restricted in any way: grains, legumes, vegetables, fruit, dairy products, and honey."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en144",
      "id": "en144r"
    }
  }, [_vm._v("96")])]), _vm._v(" Only regarding meat eating would barriers exist, but as noted earlier, it does not appear that for most people, meat was eaten often. In the first half of the biblical period, Israelites constituted the dominant majority of the inhabitants of the Land of Israel, making it unlikely that social separation was the chief rationale for the dietary laws. Although the Hebrew Bible mentions foreigners and strangers residing in the Land of Israel, neither posed a significant threat to Israelite national identity.")]), _vm._v(" "), _c('p', [_vm._v("The role of food restrictions as a bulwark between people grew in importance over time. After the destruction of Jerusalem in 586 BCE and the exile of its elite, Jews living in Babylonia become a small minority population. In the Land of Israel during the Hellenistic era, Jewish self-identification as a distinct, “holy” nation is threatened by new populations residing in the land or by Jews who wish to join the rival Greek culture. In both cases, food taboos assumed a heightened urgency for Jewish self-preservation. This is reflected in the book of Daniel, which scholars have dated to the Persian and Hellenistic periods. Daniel 1 tells of four learned and wise young men who were taken from Jerusalem to enter into service in the Babylonian court of King Nebuchadnezzar. "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p51"
    }
  }), _vm._v("With Daniel as their spokesman, the men refuse to eat the food and wine provided by the court lest they are “defiled,” and so they subsist only on legumes and water. Despite the fear expressed in the story that their health would deteriorate, “when the ten days [of testing] were over, they looked better and healthier than all the youths who were eating of the king’s food” (v. 15). Endowed with superior wisdom, they then became the king’s most trusted advisors, “ten times better than all the magicians and exorcists throughout his realm” (v. 20). Scholars differ over the reason behind the refusal of Daniel and his friends, but the most likely explanation is that in the Babylonian court, the choice of animals, the issue of blood disposal, and the eating of sacrifices made to other gods would not have conformed to Jewish practices.")]), _vm._v(" "), _c('p', [_vm._v("Additional references to food laws serving as strong social boundaries are found in the Apocrypha and Pseudepigrapha, collections of ancient Jewish books dated to the last two centuries BCE and excluded from the Hebrew Bible. The book of Jubilees presents the patriarch Abraham’s last words to his grandson Jacob as an admonition to separate himself from the nations and to “eat not with them,” because “all their ways are a pollution and an abomination and uncleanness” (22:16). The books of Tobit and Judith also depict their heroes as scrupulously avoiding the food of non-Jews."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en145",
      "id": "en145r"
    }
  }, [_vm._v("97")])]), _vm._v(" In 1 and 2 Maccabees, which tell of persecution under Antiochus IV Epiphanes, it is recorded that many pious Jews chose martyrdom rather than eat unclean food."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/nyugross0010#en146",
      "id": "en146r"
    }
  }, [_vm._v("98")])])]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0210"
    }
  }, [_vm._v("Conclusion")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("Food is not merely a source for sustenance. In the often-challenging environment of ancient Israel, with its rocky soil and minimal water, ample harvests and fertile cattle were evidence of God’s love and blessing. The Hebrew Bible emphasizes that Israel’s God is deeply concerned with what Israelites, and indeed "), _c('i', [_vm._v("all")]), _vm._v(" people, eat. The ancient system of sacrificial offerings and food rituals was designed to reciprocate and request God’s benevolence. Because Israelites believed their God was concerned not only with food but also with ethical behavior, sensitivity to life is embedded in the Torah’s food laws, albeit in ways not always obvious to the reader. Later Jews, including those in our own era, interpreted biblical food-centered texts for their understanding of how best "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p52"
    }
  }), _vm._v("to relate to the environment, animals, and other people. Because the textual and physical evidence is limited and ambiguous, unanswered questions remain about food in this thousand-year period. We do not know the extent to which the food-centered laws were widely known and practiced in ancient Israel, although we do know that the belief that God cares about Jews’ eating practices gets stronger in the Hellenistic era and becomes enshrined in postbiblical rabbinic literature. From the earliest era of Jewish history, dietary discipline and enjoyment of food were intrinsic to the ideal good life and to earning the distinction of being a nation holy to God.")]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0211"
    }
  }, [_c('span', {
    staticClass: "sm"
  }, [_vm._v("NOTES")])]), _vm._v(" "), _c('div', {
    staticClass: "notes"
  }, [_c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en49r",
      "id": "en49"
    }
  }, [_vm._v("1.")]), _vm._v(" For a survey of the kinds of sources available regarding diet in ancient Israel, see MacDonald, "), _c('i', [_vm._v("What Did the Ancient Israelites Eat?")]), _vm._v(", 10–15.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en50r",
      "id": "en50"
    }
  }, [_vm._v("2.")]), _vm._v(" Deuteronomy 11:10–11 refers to this distinction.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en51r",
      "id": "en51"
    }
  }, [_vm._v("3.")]), _vm._v(" Deuteronomy 11:13–15 (emphasis added).")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en52r",
      "id": "en52"
    }
  }, [_vm._v("4.")]), _vm._v(" See Deuteronomy 7:13, 11:14, 12:17; Jeremiah 31:11–12; Hosea 2:10, 24; Joel 2:19; Haggai 1:11; Nehemiah 5:11, 10:40; 2 Chronicles 31:5, 32:28.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en53r",
      "id": "en53"
    }
  }, [_vm._v("5.")]), _vm._v(" 2 Kings 7:1, 16.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en54r",
      "id": "en54"
    }
  }, [_vm._v("6.")]), _vm._v(" On the showbread, see Leviticus 24:5–9. Grain sacrifices are described in Leviticus 2.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en55r",
      "id": "en55"
    }
  }, [_vm._v("7.")]), _vm._v(" See Meyers, “Having Their Bread,” 21–22.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en56r",
      "id": "en56"
    }
  }, [_vm._v("8.")]), _vm._v(" Leviticus 2:11.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en57r",
      "id": "en57"
    }
  }, [_vm._v("9.")]), _vm._v(" See Genesis 40:1; Leviticus 24:5; 1 Samuel 28:24; Job 31:10; Ecclesiastes 12:3; Isaiah 47:2; Jeremiah 37:21; and Hosea 7:4.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en58r",
      "id": "en58"
    }
  }, [_vm._v("10.")]), _vm._v(" See Leviticus 26:26 and Matthew 24:41. Also see Meyers, “Having Their Bread,” 14–44; Meyers, “In the Household and Beyond,” 24–25; Meyers, “From Field Crops to Food,” 70–78.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en59r",
      "id": "en59"
    }
  }, [_vm._v("11.")]), _vm._v(" Leviticus 24:1–4.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en60r",
      "id": "en60"
    }
  }, [_vm._v("12.")]), _vm._v(" For example, see Exodus 25:6, 29:7, 29:21, 31:11, 35:8; Leviticus 8:2, 8:10, 21:10; 1 Samuel 10, 16; and 1 Kings 1.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en61r",
      "id": "en61"
    }
  }, [_vm._v("13.")]), _vm._v(" See Ecclesiastes 9:8; Ezekiel 16:4, 9.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en62r",
      "id": "en62"
    }
  }, [_vm._v("14.")]), _vm._v(" Walsh, "), _c('i', [_vm._v("The Fruit of the Vine")]), _vm._v(", 29–40.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en63r",
      "id": "en63"
    }
  }, [_vm._v("15.")]), _vm._v(" See Exodus 17; Numbers 20; 1 Samuel 30:12.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en64r",
      "id": "en64"
    }
  }, [_vm._v("16.")]), _vm._v(" Numbers 13:23.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en65r",
      "id": "en65"
    }
  }, [_vm._v("17.")]), _vm._v(" Lamentations 2:12.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en66r",
      "id": "en66"
    }
  }, [_vm._v("18.")]), _vm._v(" Lichtheim, “Sinuhe,” 1:79. Also see Jaggard, “Huge Wine Cellar Unearthed.”")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en67r",
      "id": "en67"
    }
  }, [_vm._v("19.")]), _vm._v(" See Exodus 29:40; Leviticus 23:13; Numbers 15:5, 15:7; Deuteronomy 14:26.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en68r",
      "id": "en68"
    }
  }, [_vm._v("20.")]), _vm._v(" See Judges 9:13; Psalms 104:15; Ecclesiastes 9:7, 10:19.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en69r",
      "id": "en69"
    }
  }, [_vm._v("21.")]), _vm._v(" See Genesis 9:20–27, 19:30–38; Leviticus 10:9; Numbers 6:3–4; Isaiah 28:7–8.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p53"
    }
  }), _c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en70r",
      "id": "en70"
    }
  }, [_vm._v("22.")]), _vm._v(" "), _c('i', [_vm._v("Devash")]), _vm._v(" refers to bees’ honey in at least two passages (Judges 14:8 and 1 Samuel 14:25), but elsewhere date syrup is assumed; see MacDonald, "), _c('i', [_vm._v("What Did the Ancient Israelites Eat?")]), _vm._v(", 39–40; Milgrom, "), _c('i', [_vm._v("Leviticus 1–16")]), _vm._v(", 189–90; Borowski, "), _c('i', [_vm._v("Every Living Thing")]), _vm._v(", 161–63.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en71r",
      "id": "en71"
    }
  }, [_vm._v("23.")]), _vm._v(" See Job 10:10; 2 Samuel 17:29.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en72r",
      "id": "en72"
    }
  }, [_vm._v("24.")]), _vm._v(" Deuteronomy 8:7–9.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en73r",
      "id": "en73"
    }
  }, [_vm._v("25.")]), _vm._v(" Anderson, “Sacrifice and Sacrificial Offerings,” 5:872. He calls sacrifice a multivalent entity, as “various explanations of its function could coexist.” Milgrom reviews different interpretations (Milgrom, "), _c('i', [_vm._v("Leviticus 1–16")]), _vm._v(", 440–43).")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en74r",
      "id": "en74"
    }
  }, [_vm._v("26.")]), _vm._v(" Leviticus 7:28–34 dictates the portions given to the priesthood. See also Leviticus 17:4, which asserts that killing an animal for food without the necessary rituals is bloodshed, akin to homicide.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en75r",
      "id": "en75"
    }
  }, [_vm._v("27.")]), _vm._v(" Numbers 15:1–5.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en76r",
      "id": "en76"
    }
  }, [_vm._v("28.")]), _vm._v(" See Leviticus 27:26, 27:30–32; Numbers 18:13–24; Deuteronomy 14:22–29, 18:4, 26:2.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en77r",
      "id": "en77"
    }
  }, [_vm._v("29.")]), _vm._v(" Deuteronomy 26:1–11.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en78r",
      "id": "en78"
    }
  }, [_vm._v("30.")]), _vm._v(" See Deuteronomy 12:7, 12:11–12, 16:11, 16:14, 16:15; Samuel 20:5, 20:24.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en79r",
      "id": "en79"
    }
  }, [_vm._v("31.")]), _vm._v(" The three pilgrimage festivals are discussed in Exodus 23:14–17, 34:18–23; Leviticus 23; and Deuteronomy 16. Baruch Levine notes that “in earlier times, the pilgrimage might have brought a family to a nearby altar,” but Deuteronomy ordains that all sacrificial offerings be brought to one, central Sanctuary ("), _c('i', [_vm._v("Leviticus")]), _vm._v(", 156).")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en80r",
      "id": "en80"
    }
  }, [_vm._v("32.")]), _vm._v(" Biblical references for Passover are Exodus 12:34, 12:39; Deuteronomy 16:3–4. For Shavuot, see Leviticus 23:14. For Sukkot, see Exodus 23:16, 34:22; Leviticus 23:33–36; Nehemiah 8:13–18; and Zechariah 14:18–19.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en81r",
      "id": "en81"
    }
  }, [_vm._v("33.")]), _vm._v(" Genesis 1:24–26, 2:7, 2:19.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en82r",
      "id": "en82"
    }
  }, [_vm._v("34.")]), _vm._v(" Genesis 8:8–17.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en83r",
      "id": "en83"
    }
  }, [_vm._v("35.")]), _vm._v(" See Genesis 9:5–6; Exodus 21:28; Leviticus 20:15–16.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en84r",
      "id": "en84"
    }
  }, [_vm._v("36.")]), _vm._v(" Leviticus 17:4. See Milgrom, "), _c('i', [_vm._v("Leviticus 17–22")]), _vm._v(", 1457. He writes, “Elsewhere this phrase [‘he has shed blood’] connotes the intentional murder of a human being.”")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en85r",
      "id": "en85"
    }
  }, [_vm._v("37.")]), _vm._v(" The prohibition of consuming blood is stated five times in the Torah: Genesis 9:4; Leviticus 3:17, 7:26–27, 17:12–15; Deuteronomy 12:16. This prohibition is assumed in an early narrative, 1 Samuel 14:33. Sperling writes that the covering of blood reflects the idea that “what offends God should be hidden from his sight” as in Deuteronomy 23:14–15; Ezekiel 24:7–8 (Sperling, “Blood,” 1:761–64). See also Milgrom, "), _c('i', [_vm._v("Leviticus 1–16")]), _vm._v(", 704–6.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en86r",
      "id": "en86"
    }
  }, [_vm._v("38.")]), _vm._v(" For humankind’s evil nature, see Genesis 8:21. Gary Rendsburg terms Genesis 9:4’s permission to eat meat “a divine acceptance of human inability to adhere to the utopia established at creation”; Rendsburg, “Vegetarian Ideal in the Bible,” 322.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en87r",
      "id": "en87"
    }
  }, [_vm._v("39.")]), _vm._v(" Milgrom, "), _c('i', [_vm._v("Leviticus 1–16")]), _vm._v(", 733. See also Kraemer, "), _c('i', [_vm._v("Jewish Eating")]), _vm._v(", 15–16; MacDonald, "), _c('i', [_vm._v("What Did the Ancient Israelites Eat?")]), _vm._v(", 73–75, who cites others; Cooper, "), _c('i', [_vm._v("Eat and Be Satisfied")]), _vm._v(", 3.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p54"
    }
  }), _c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en88r",
      "id": "en88"
    }
  }, [_vm._v("40.")]), _vm._v(" Leviticus 19:5–7.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en89r",
      "id": "en89"
    }
  }, [_vm._v("41.")]), _vm._v(" 1 Kings 5:2–3.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en90r",
      "id": "en90"
    }
  }, [_vm._v("42.")]), _vm._v(" Laws regarding plant life are found in Leviticus 19:9–10, 19:23–25, 23:22, 25:1–12; Deuteronomy 20:19–20, 23:25–26, 24:19–21.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en91r",
      "id": "en91"
    }
  }, [_vm._v("43.")]), _vm._v(" Leviticus 11:3; Deuteronomy 14:6.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en92r",
      "id": "en92"
    }
  }, [_vm._v("44.")]), _vm._v(" See Milgrom, "), _c('i', [_vm._v("Leviticus 1–16")]), _vm._v(", 718–19; Rabinowicz, “Dietary Laws,” 6:43.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en93r",
      "id": "en93"
    }
  }, [_vm._v("45.")]), _vm._v(" Philo Judaeus, “Special Laws,” book 4, 18–20.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en94r",
      "id": "en94"
    }
  }, [_vm._v("46.")]), _vm._v(" Kraemer, "), _c('i', [_vm._v("Jewish Eating")]), _vm._v(", 21–22. Kraemer bases his ideas on Howard Eilberg-Schwartz, “Animal Metaphors,” 2–22.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en95r",
      "id": "en95"
    }
  }, [_vm._v("47.")]), _vm._v(" For discussion, see Milgrom, "), _c('i', [_vm._v("Leviticus 1–16")]), _vm._v(", 725–33.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en96r",
      "id": "en96"
    }
  }, [_vm._v("48.")]), _vm._v(" Kraemer, "), _c('i', [_vm._v("Jewish Eating")]), _vm._v(", 18. See also MacDonald, "), _c('i', [_vm._v("What Did the Ancient Israelites Eat?")]), _vm._v(", 64.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en97r",
      "id": "en97"
    }
  }, [_vm._v("49.")]), _vm._v(" For discussion, see Levine, "), _c('i', [_vm._v("Leviticus")]), _vm._v(", 243–48.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en98r",
      "id": "en98"
    }
  }, [_vm._v("50.")]), _vm._v(" Milgrom, "), _c('i', [_vm._v("Leviticus 1–16")]), _vm._v(", 716.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en99r",
      "id": "en99"
    }
  }, [_vm._v("51.")]), _vm._v(" See Leviticus 1:6, 17:13; Deuteronomy 12:16, 12:22–23.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en100r",
      "id": "en100"
    }
  }, [_vm._v("52.")]), _vm._v(" Leviticus 7:26.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en101r",
      "id": "en101"
    }
  }, [_vm._v("53.")]), _vm._v(" See Firmage, “Zoology,” 6:1112–13. The Torah associates hunting with generally negative characters (Genesis 10:8–10 for Nimrod, Genesis 21:20 for Ishmael, and Genesis 25:19–34, 25:27 for Esau).")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en102r",
      "id": "en102"
    }
  }, [_vm._v("54.")]), _vm._v(" Pits, nets, and traps are mentioned in Isaiah 24:17; Jeremiah 16:15; Ezekiel 19:8 (for lions); Psalms 7:16, 35:7, 140:6; Proverbs 22:14, 26:27.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en103r",
      "id": "en103"
    }
  }, [_vm._v("55.")]), _vm._v(" David mentions attacks by lions and bears on his flock animals (1 Samuel 17:34–37). Lion attacks are mentioned also in Judges 14:5; 1 Kings 13:24; 2 Kings 17:25–26; and elsewhere.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en104r",
      "id": "en104"
    }
  }, [_vm._v("56.")]), _vm._v(" Leviticus 3:16–17, 7:22–25. Deuteronomy 12 does not mention "), _c('i', [_vm._v("helev")]), _vm._v(" in its law permitting nonsacrificial meat eating, thus it permits it outside of a sacrificial context.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en105r",
      "id": "en105"
    }
  }, [_vm._v("57.")]), _vm._v(" Exodus 23:19, 34:26; Deuteronomy 14:21.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en106r",
      "id": "en106"
    }
  }, [_vm._v("58.")]), _vm._v(" Tigay, "), _c('i', [_vm._v("Deuteronomy")]), _vm._v(", 140.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en107r",
      "id": "en107"
    }
  }, [_vm._v("59.")]), _vm._v(" Propp, "), _c('i', [_vm._v("Exodus 19–40")]), _vm._v(", 286.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en108r",
      "id": "en108"
    }
  }, [_vm._v("60.")]), _vm._v(" For discussion, see Hesse, “Animal Husbandry and Human Diet,” 1:215; Firmage, “Zoology,” 6:1131, 1134–35.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en109r",
      "id": "en109"
    }
  }, [_vm._v("61.")]), _vm._v(" “Pig bones typically constitute only a fraction of 1% or are entirely absent,” writes Dever, "), _c('i', [_vm._v("Who Were the Early Israelites?")]), _vm._v(", 108.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en110r",
      "id": "en110"
    }
  }, [_vm._v("62.")]), _vm._v(" See Judges 13:7; Ezekiel 4:14; Daniel 1.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en111r",
      "id": "en111"
    }
  }, [_vm._v("63.")]), _vm._v(" Miller, “Hogs and Hygiene,” 125–40.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en112r",
      "id": "en112"
    }
  }, [_vm._v("64.")]), _vm._v(" See Owen, “Pigs and Pig By-Products at Garsana,” 75–87.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en113r",
      "id": "en113"
    }
  }, [_vm._v("65.")]), _vm._v(" For this idea, see Firmage, “Zoology,” 6:1133–34.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en114r",
      "id": "en114"
    }
  }, [_vm._v("66.")]), _vm._v(" Leviticus 11:10; Deuteronomy 14:9–10.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en115r",
      "id": "en115"
    }
  }, [_vm._v("67.")]), _vm._v(" Genesis 9:2; Numbers 11:5; Nehemiah 13:16.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en116r",
      "id": "en116"
    }
  }, [_vm._v("68.")]), _vm._v(" Job 40:31 speaks of spears for fishing, Ecclesiastes 9:12 portrays humankind as “fish enmeshed in a fatal net,” while Habakkuk 1:14–16 mentions hooks and nets. See also Isaiah 19:8; Ezekiel 29:4, 47:10; Job 40:25–26; Ecclesiastes 7:26.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p55"
    }
  }), _c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en117r",
      "id": "en117"
    }
  }, [_vm._v("69.")]), _vm._v(" Borowski, "), _c('i', [_vm._v("Every Living Thing")]), _vm._v(", 168–75; MacDonald, "), _c('i', [_vm._v("What Did the Ancient Israelites Eat?")]), _vm._v(", 37–38.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en118r",
      "id": "en118"
    }
  }, [_vm._v("70.")]), _vm._v(" Borowski, "), _c('i', [_vm._v("Every Living Thing")]), _vm._v(", 176; MacDonald, "), _c('i', [_vm._v("What Did the Ancient Israelites Eat?")]), _vm._v(", 38.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en119r",
      "id": "en119"
    }
  }, [_vm._v("71.")]), _vm._v(" Milgrom, "), _c('i', [_vm._v("Leviticus 1–16")]), _vm._v(", 661–64.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en120r",
      "id": "en120"
    }
  }, [_vm._v("72.")]), _vm._v(" Amos 3:5; Hosea 7:12; Psalm 124:7; Proverbs 6:5, 7:23; Lamentations 3:52.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en121r",
      "id": "en121"
    }
  }, [_vm._v("73.")]), _vm._v(" Isaiah 60:8 and Jeremiah 5:27–28 both speak of birds kept in cages. The law of Deuteronomy 22:6 mentions the collection of the eggs of wild birds.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en122r",
      "id": "en122"
    }
  }, [_vm._v("74.")]), _vm._v(" Leviticus 1:14, 5:7; Milgrom, "), _c('i', [_vm._v("Leviticus 1–16")]), _vm._v(", 166–68.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en123r",
      "id": "en123"
    }
  }, [_vm._v("75.")]), _vm._v(" Art from Iron Age Israel depicts roosters in a fighting stance; therefore, it is possible that they were used for cock fighting and only later used for eating. See MacDonald, "), _c('i', [_vm._v("What Did the Ancient Israelites Eat?")]), _vm._v(", 36–37; Borowski, "), _c('i', [_vm._v("Every Living Thing")]), _vm._v(", 176–78.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en124r",
      "id": "en124"
    }
  }, [_vm._v("76.")]), _vm._v(" Mark 1:6; Matthew 3:4. Also see Borowski, "), _c('i', [_vm._v("Every Living Thing")]), _vm._v(", 159.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en125r",
      "id": "en125"
    }
  }, [_vm._v("77.")]), _vm._v(" Milgrom, "), _c('i', [_vm._v("Leviticus 1–16")]), _vm._v(", 734. See also Kelhoffer, “Did John the Baptist?,” 302.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en126r",
      "id": "en126"
    }
  }, [_vm._v("78.")]), _vm._v(" Flint-Hamilton, “Legumes in Ancient Greece and Rome,” 371–85.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en127r",
      "id": "en127"
    }
  }, [_vm._v("79.")]), _vm._v(" 2 Kings 4 mentions the consumption of wild plants, a vine and gourds, during a famine. In this instance, they prove to be poisonous.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en128r",
      "id": "en128"
    }
  }, [_vm._v("80.")]), _vm._v(" The link between meat eating and joy is most obvious in Deuteronomy and in its use of the verb "), _c('i', [_vm._v("samah")]), _vm._v(" (Deuteronomy 12:7, 12:12, 12:18, 14:23–27, 16:11–14, 26:11, 27:7). See also Tigay, "), _c('i', [_vm._v("Deuteronomy")]), _vm._v(", 122; Anderson, "), _c('i', [_vm._v("Time to Mourn")]), _vm._v(", 19–26.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en129r",
      "id": "en129"
    }
  }, [_vm._v("81.")]), _vm._v(" Numbers 13:23 says that the spies sent by Moses brought back fruit (pomegranates, grapes, and dates) as samples of the produce of the land.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en130r",
      "id": "en130"
    }
  }, [_vm._v("82.")]), _vm._v(" A fruit called "), _c('i', [_vm._v("dudaim")]), _vm._v(" is mentioned in Genesis 30:14 and Song of Songs 7:14 and is usually identified as a mandrake, which was believed to enhance sexual arousal and fertility. The "), _c('i', [_vm._v("tapuah")]), _vm._v(" is mentioned in Joel 1:12; Proverbs 25:11; and Song of Songs 2:3, 5:5, 7:9, and 8:5. For the identification of "), _c('i', [_vm._v("tapuah")]), _vm._v(" as apple, see MacDonald, "), _c('i', [_vm._v("What Did the Ancient Israelites Eat?")]), _vm._v(", 30. The ubiquity of the sycamore is suggested by 1 Kings 10:27. Watermelon is mentioned in Numbers 11:5.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en131r",
      "id": "en131"
    }
  }, [_vm._v("83.")]), _vm._v(" 1 Kings 5:5.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en132r",
      "id": "en132"
    }
  }, [_vm._v("84.")]), _vm._v(" The fruit of an "), _c('i', [_vm._v("etrog")]), _vm._v(" or citron is inedible, although its peel or rind can be candied or used for flavoring. Milgrom concludes, “In sum, the midrashic interpretations of the rabbis notwithstanding, the identification of this ‘fruit’ with the citron in doubtful” (Milgrom, "), _c('i', [_vm._v("Leviticus 23–27")]), _vm._v(", 2041).")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en133r",
      "id": "en133"
    }
  }, [_vm._v("85.")]), _vm._v(" The term "), _c('i', [_vm._v("‘egoz")]), _vm._v(" (Song of Songs 6:11) refers to nuts in general, not a specific kind of nut. Pistachios and almonds are mentioned in Genesis 43:11. Almond trees or branches are mentioned in Numbers 17:23; Jeremiah 1:11; and Ecclesiastes 12:5.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en134r",
      "id": "en134"
    }
  }, [_vm._v("86.")]), _vm._v(" Job 6:6.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en135r",
      "id": "en135"
    }
  }, [_vm._v("87.")]), _vm._v(" Coriander is mentioned to describe the appearance of manna (Exodus 16:31; Numbers 11:7), while Isaiah 28:25, 28:27 mentions cumin and black cumin, "), _c('i', [_vm._v("kamon")]), _vm._v(" and "), _c('i', [_vm._v("qezah")]), _vm._v(". Song of Songs 4:14 refers to saffron.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en136r",
      "id": "en136"
    }
  }, [_vm._v("88.")]), _vm._v(" MacDonald, "), _c('i', [_vm._v("What Did the Ancient Israelites Eat?")]), _vm._v(", 91–93.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en137r",
      "id": "en137"
    }
  }, [_vm._v("89.")]), _vm._v(" Garnsey, “Famine in History,” 145–50.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p56"
    }
  }), _c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en138r",
      "id": "en138"
    }
  }, [_vm._v("90.")]), _vm._v(" MacDonald, "), _c('i', [_vm._v("What Did the Ancient Israelites Eat?")]), _vm._v(", 87.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en139r",
      "id": "en139"
    }
  }, [_vm._v("91.")]), _vm._v(" Genesis 12, 20, 26, 41–42; Ruth 1; 1 Kings 17.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en140r",
      "id": "en140"
    }
  }, [_vm._v("92.")]), _vm._v(" Leviticus 26:29; Deuteronomy 28:57; 2 Kings 6:24–30; Lamentations 2:20, 4:10; Ezekiel 5:10. See MacDonald, "), _c('i', [_vm._v("What Did the Ancient Israelites Eat?")]), _vm._v(", 60.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en141r",
      "id": "en141"
    }
  }, [_vm._v("93.")]), _vm._v(" Ruth 2.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en142r",
      "id": "en142"
    }
  }, [_vm._v("94.")]), _vm._v(" Leviticus 19:13; Deuteronomy 24:6, 24:15.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en143r",
      "id": "en143"
    }
  }, [_vm._v("95.")]), _vm._v(" Leviticus 11:43–45 speaks of sanctification, but the goal of separating Israel from the nations doesn’t appear until the summary passage in Leviticus 20:24, 20:26. Other passages that emphasize holiness are Exodus 22:30 and Deuteronomy 14:21, both of which concern the taboo of consuming blood. Ethnic distinction as a basis for dietary laws is evident in Deuteronomy 14, as the food prohibitions are preceded and followed by mention of Israel as a “holy people” (vv. 1–2 and v. 21).")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en144r",
      "id": "en144"
    }
  }, [_vm._v("96.")]), _vm._v(" Kraemer, "), _c('i', [_vm._v("Jewish Eating")]), _vm._v(", 19–23.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en145r",
      "id": "en145"
    }
  }, [_vm._v("97.")]), _vm._v(" Tobit 1:10–13; Judith 12:1–2.")])]), _vm._v(" "), _c('div', {
    staticClass: "endnote"
  }, [_c('p', {
    staticClass: "en"
  }, [_c('a', {
    staticClass: "ennum",
    attrs: {
      "href": "#/nyugross0010#en146r",
      "id": "en146"
    }
  }, [_vm._v("98.")]), _vm._v(" 1 Maccabees 1:47, 1:62; 2 Maccabees 6:18–7:42.")])])]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0212"
    }
  }, [_c('span', {
    staticClass: "sm"
  }, [_vm._v("BIBLIOGRAPHY")])]), _vm._v(" "), _c('div', {
    staticClass: "references"
  }, [_c('p', {
    staticClass: "rf"
  }, [_vm._v("Anderson, Gary A. “Sacrifice and Sacrificial Offerings (Old Testament).” In "), _c('i', [_vm._v("Anchor Bible Dictionary")]), _vm._v(", vol. 5, edited by David Noel Freedman. New York: Doubleday, 1992.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("———. "), _c('i', [_vm._v("A Time to Mourn, a Time to Dance")]), _vm._v(". University Park: Pennsylvania State University, 1991.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Borowski, Oded. "), _c('i', [_vm._v("Every Living Thing: Daily Use of Animals in Ancient Israel")]), _vm._v(". Walnut Creek, CA: AltaMira, 1998.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Cassuto, M. D. "), _c('i', [_vm._v("Commentary on Genesis")]), _vm._v(". [In Hebrew.] Jerusalem: Magnes, 1978.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Coogan, Michael. “In the Beginning: The Earliest History.” In "), _c('i', [_vm._v("The Oxford History of the Biblical World")]), _vm._v(", edited by Michael D. Coogan, 3–24. New York: Oxford University Press, 1998.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Cooper, John. "), _c('i', [_vm._v("Eat and Be Satisfied: A Social History of Jewish Food")]), _vm._v(". Northvale, NJ: Jason Aronson, 1993.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Dever, William. "), _c('i', [_vm._v("Who Were the Israelites and Where Did They Come From?")]), _vm._v(" Grand Rapids, MI: William B. Eerdmans, 2003.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Eilberg-Schwartz, Howard. “Animal Metaphors in the Ritual and Narratives of Ancient Israel.” "), _c('i', [_vm._v("Journal of Ritual Studies")]), _vm._v(" 2 (1988): 2–22.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Firmage, Edward. “Zoology.” In "), _c('i', [_vm._v("Anchor Bible Dictionary")]), _vm._v(", edited by David Noel Freedman. New York: Doubleday, 1992.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Flint-Hamilton, Kimberly B. “Legumes in Ancient Greece and Rome: Food, Medicine, or Poison?” "), _c('i', [_vm._v("Hesperia")]), _vm._v(" 68 (1999): 371–85.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Garnsey, Peter. “Famine in History.” In "), _c('i', [_vm._v("Understanding Catastrophe")]), _vm._v(", edited by J. Bourriau, 145–78. Cambridge: Cambridge University Press, 1990.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Goodfriend, Elaine. “Leviticus 22:24: A Prohibition of Gelding for the Land of Israel?” In "), _c('i', [_vm._v("Current Issues in Priestly and Related Literature: The Legacy of Jacob Milgrom and Beyond")]), _vm._v(", edited by Roy E. Gane and Ada Taggar Cohen, 67–92. Atlanta: SBL, 2015.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p57"
    }
  }), _vm._v("Hesse, Brian. “Animal Husbandry and Human Diet in the Ancient Near East.” In "), _c('i', [_vm._v("Civilizations of the Ancient Near East")]), _vm._v(", vol. 1, edited by Jack M. Sasson, 203–22. Peabody, MA: Hendrickson, 2000.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Houston, Walter. "), _c('i', [_vm._v("Purity and Monotheism: Clean and Unclean Animals in Biblical Law")]), _vm._v(". Sheffield, England: Sheffield, 1993.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Jaggard, Victoria. “Huge Wine Cellar Unearthed at a Biblical Era Palace in Israel.” "), _c('i', [_vm._v("Smithsonian")]), _vm._v(", August 27, 2014. "), _c('a', {
    staticClass: "url",
    attrs: {
      "href": "http://www.smithsonianmag.com",
      "target": "_blank"
    }
  }, [_vm._v("www.smithsonianmag.com")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Kelhoffer, James A. “Did John the Baptist Eat Like a Former Essene? Locust-Eating in the Ancient Near East and at Qumran.” "), _c('i', [_vm._v("Dead Sea Discoveries")]), _vm._v(" 11 (2003): 293–314.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Kraemer, David. "), _c('i', [_vm._v("Jewish Eating and Identity through the Ages")]), _vm._v(". New York: Routledge, 2007.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Levine, Baruch. "), _c('i', [_vm._v("Leviticus")]), _vm._v(". Philadelphia: Jewish Publication Society, 1989.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Lichtheim, Miriam. “Sinuhe.” In "), _c('i', [_vm._v("The Context of Scripture")]), _vm._v(", 77–82. Vol. 1 of "), _c('i', [_vm._v("Canonical Compositions from the Biblical World")]), _vm._v(", edited by William W. Hallo and K. Lawson Younger Jr. Leiden: E. J. Brill, 2003.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("MacDonald, Nathan. "), _c('i', [_vm._v("What Did the Ancient Israelites Eat? Diet in Biblical Times")]), _vm._v(". Grand Rapids, MI: William B. Eerdmans, 2008.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Matthews, Victor H. “Treading the Winepress: Real and Metaphorical Viticulture in the Ancient Near East.” "), _c('i', [_vm._v("Semeia")]), _vm._v(" 86 (1999): 19–32.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Meyers, Carol. “From Field Crops to Food: Attributing Gender and Meaning to Bread Production in Iron Age Israel.” In "), _c('i', [_vm._v("The Archeology of Difference: Gender, Ethnicity, Class, and the “Other” in Antiquity: Studies in Honor of Eric M. Meyers")]), _vm._v(", edited by Douglas R. Edwards and C. Thomas McCollough, 67–84. Annual of the American Schools of Oriental Research 60/61. Boston: American Schools of Oriental Research, 2007.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("———. “Having Their Bread and Eating There Too: Bread Production and Female Power in Ancient Israelite Households.” "), _c('i', [_vm._v("Nashim")]), _vm._v(" 5 (2002): 14–44.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("———. “In the Household and Beyond: The Social World of Israelite Women.” "), _c('i', [_vm._v("Nordic Journal of Theology")]), _vm._v(" 63 (2009): 19–41.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Milgrom, Jacob. "), _c('i', [_vm._v("Leviticus 1–16: A New Translation with Introduction and Commentary")]), _vm._v(". Anchor Bible Series 3. New York: Doubleday, 1991.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("———. "), _c('i', [_vm._v("Leviticus 17–22: A New Translation with Introduction and Commentary")]), _vm._v(". Anchor Bible Series 3. New York: Doubleday, 2000.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("———. "), _c('i', [_vm._v("Leviticus 23–27: A New Translation with Introduction and Commentary")]), _vm._v(". Anchor Bible Series 3. New York: Doubleday, 2001.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Miller, Robert L. “Hogs and Hygiene.” "), _c('i', [_vm._v("Journal of Egyptian Archaeology")]), _vm._v(" 76 (1990): 125–40.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Owen, David. “Pigs and Pig By-Products at Garsana in the Ur III Period.” In "), _c('i', [_vm._v("De la domestication au taboo: Le cas des suides dans le Proche-Orient ancient")]), _vm._v(", edited by Brigitte Lion, 75–87. France: de Boccard, 2006.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Philo Judaeus. “The Special Laws.” Early Jewish Writings. "), _c('a', {
    staticClass: "url",
    attrs: {
      "href": "http://www.earlyjewishwritings.com",
      "target": "_blank"
    }
  }, [_vm._v("www.earlyjewishwritings.com")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Propp, William H. C. "), _c('i', [_vm._v("Exodus 19–40")]), _vm._v(". The Anchor Bible 2a. New York: Doubleday, 2006.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p58"
    }
  }), _vm._v("Rabinowicz, Harry. “Dietary Laws.” In "), _c('i', [_vm._v("Encyclopedia Judaica")]), _vm._v(". New York: Macmillan, 1972.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Rendsburg, Gary. “The Vegetarian Ideal in the Bible.” In "), _c('i', [_vm._v("Food and Judaism")]), _vm._v(", edited by L. J. Greenspoon, R. A. Simkins, and G. Shapiro, 319–34. Omaha: Creighton University Press, 2005.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Sarna, Nahum. "), _c('i', [_vm._v("Genesis")]), _vm._v(". Philadelphia: Jewish Publication Society, 1989.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Sperling, C. David. “Blood.” In "), _c('i', [_vm._v("Anchor Bible Dictionary")]), _vm._v(", vol. 1, edited by David Noel Freedman. New York: Doubleday, 1992.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Tigay, Jeffrey. "), _c('i', [_vm._v("Deuteronomy")]), _vm._v(". Philadelphia: Jewish Publication Society, 1989.")]), _vm._v(" "), _c('p', {
    staticClass: "rf"
  }, [_vm._v("Walsh, Carey. "), _c('i', [_vm._v("The Fruit of the Vine: Viticulture in Ancient Israel")]), _vm._v(". Harvard Semitic Museum Publications. Winona Lake, IN: Eisenbrauns, 2000.")])])])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-60b21dee","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/nyugross0010.vue
// module id = 81
// module chunks = 0